import { useId, useState } from 'react';
import { useFormSubmit } from '../lib/forms';
import { formatInt } from '../lib/ui';
import { FormStatusMessage } from './FormStatusMessage';
import { Honeypot } from './Honeypot';
import { Icon } from './Icon';

const PROJECT_TYPES = [
  { value: 'residential', label: 'Residential' },
  { value: 'villa', label: 'Villa' },
  { value: 'office', label: 'Office' },
  { value: 'commercial', label: 'Commercial' },
];

const field =
  'w-full bg-surface px-4 py-2.5 rounded border border-border-muted font-body-sm text-on-surface focus:outline-none focus:border-gold-dark';
const label = 'block font-label-md text-label-md uppercase tracking-wider text-primary mb-1';

/** "Private Consultation Inquiry" card used on the home page. */
export function ConsultationForm({ headingLevel = 'h3' }: { headingLevel?: 'h2' | 'h3' }) {
  const { status, error, onSubmit } = useFormSubmit('consultation');
  const [area, setArea] = useState(2400);
  const id = useId();
  const Heading = headingLevel;

  return (
    <div className="bg-surface-ivory text-on-surface p-space-lg md:p-space-xl rounded-xl shadow-2xl">
      <div className="flex items-center justify-between border-b border-border-muted pb-space-sm mb-space-md">
        <div>
          <Heading className="font-headline-sm text-headline-sm text-primary">Private Consultation Inquiry</Heading>
          <p className="font-body-sm text-body-sm text-on-surface-variant">Confidential estimate &amp; architectural scoping</p>
        </div>
        <Icon name="design_services" size={28} className="text-gold-dark" />
      </div>

      <form className="relative flex flex-col gap-space-md" onSubmit={onSubmit}>
        <Honeypot />
        <fieldset>
          <legend className={label + ' mb-2'}>1. Select Project Type</legend>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            {PROJECT_TYPES.map((t, i) => (
              <label key={t.value} className="cursor-pointer">
                <input className="peer sr-only" name="project_type" type="radio" value={t.value} defaultChecked={i === 0} />
                <span className="block p-2 text-center rounded bg-surface text-on-surface font-label-sm text-label-sm uppercase border border-border-muted peer-checked:bg-primary peer-checked:text-gold-light peer-focus-visible:ring-2 peer-focus-visible:ring-gold-dark transition-all">
                  {t.label}
                </span>
              </label>
            ))}
          </div>
        </fieldset>

        <div>
          <div className="flex justify-between items-center mb-1 gap-2">
            <label htmlFor={`${id}-area`} className="font-label-md text-label-md uppercase tracking-wider text-primary">
              2. Approximate Area (Sq. Ft.)
            </label>
            <output htmlFor={`${id}-area`} className="font-headline-sm text-headline-sm text-primary font-semibold whitespace-nowrap">
              {formatInt(area)} sq.ft
            </output>
          </div>
          <input
            id={`${id}-area`}
            name="area_sqft"
            className="w-full accent-primary h-2 bg-surface-container rounded-lg cursor-pointer"
            type="range"
            min={500}
            max={15000}
            step={100}
            value={area}
            onChange={(e) => setArea(Number(e.target.value))}
          />
          <div className="flex justify-between text-[11px] text-on-surface-variant font-label-sm uppercase mt-1 gap-2">
            <span>500 sq.ft (Studio/Suite)</span>
            <span className="text-right">15,000+ sq.ft (Estate/Commercial)</span>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-space-md">
          <div>
            <label htmlFor={`${id}-name`} className={label}>Full Name</label>
            <input id={`${id}-name`} name="name" className={field} placeholder="e.g. S. Ramanathan" required autoComplete="name" type="text" />
          </div>
          <div>
            <label htmlFor={`${id}-phone`} className={label}>Direct Phone</label>
            <input id={`${id}-phone`} name="phone" className={field} placeholder="+91 000 000 0000" required autoComplete="tel" type="tel" />
          </div>
        </div>
        <div>
          <label htmlFor={`${id}-notes`} className={label}>Project Notes / Location</label>
          <textarea
            id={`${id}-notes`}
            name="message"
            className={field + ' resize-none'}
            placeholder="Mention project location (e.g. Nagercoil, Kanyakumari, Trivandrum) and key styling desires..."
            rows={2}
          />
        </div>

        <button
          type="submit"
          disabled={status === 'submitting'}
          className="w-full bg-primary hover:bg-primary-container text-gold-light hover:text-on-primary font-label-lg text-label-lg uppercase tracking-widest py-space-md rounded transition-all duration-300 shadow-[0_4px_20px_rgba(3,37,23,0.25)] flex items-center justify-center gap-space-sm disabled:opacity-70"
        >
          <span>{status === 'submitting' ? 'Sending…' : 'Submit & Request Preliminary Plan'}</span>
          <Icon name={status === 'submitting' ? 'progress_activity' : 'send'} size={18} className={status === 'submitting' ? 'animate-spin' : ''} />
        </button>
        <FormStatusMessage
          status={status}
          error={error}
          successText="Thank you. A Green Valley design advisor will contact you within 24 hours."
        />
      </form>
    </div>
  );
}
